import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { streamRun } from '../api/sse';
import { useApi, useTokenGetter } from '../hooks/useApi';
import type { ContentBlock, MessageOut } from '../types/api';
import { useConversations } from './ConversationsContext';

export type RunState = 'idle' | 'sending' | 'streaming' | 'error';

export interface PendingAssistant {
  /** Content blocks indexed by their position; rendered in numeric order. */
  blocks: Record<number, ContentBlock>;
  runId: string | null;
  /**
   * React key for the streaming bubble. The finalized assistant message is
   * given this same id, so MessageList reuses the same DOM node across the
   * stream→finalize transition.
   */
  localId: string;
}

interface ActiveConversationContextValue {
  conversationId: string | null;
  messages: MessageOut[];
  loading: boolean;
  loadError: string | null;
  runState: RunState;
  runError: string | null;
  pending: PendingAssistant | null;
  send: (text: string) => Promise<void>;
  stop: () => void;
}

const ActiveConversationContext = createContext<ActiveConversationContextValue | null>(null);

interface ProviderProps {
  /** Route param: either 'new' (lazy create on first send) or a UUID. */
  paramId: string;
  children: ReactNode;
}

function makeLocalId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Reconcile poll cadence and overall cap, overridable per environment. */
const RECONCILE_POLL_INTERVAL_MS =
  Number(import.meta.env.VITE_RECONCILE_POLL_INTERVAL_MS) || 1500;
const RECONCILE_TIMEOUT_MS = Number(import.meta.env.VITE_RECONCILE_TIMEOUT_MS) || 90000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Merge server messages into the currently displayed list, preserving React
 * keys. Messages already on screen — often the just-streamed exchange carrying
 * optimistic local ids — keep their id so they update in place instead of
 * remounting (a remount replays the entry animation, i.e. the "flash"). New
 * messages (from another device, or the freshly finished reply) keep their
 * server id and mount. Safe because conversations are append-only and ordered,
 * so positional role-matching aligns existing rows.
 */
function mergeMessages(prev: MessageOut[], next: MessageOut[]): MessageOut[] {
  return next.map((serverMsg, i) => {
    const local = prev[i];
    return local && local.role === serverMsg.role
      ? { ...serverMsg, id: local.id }
      : serverMsg;
  });
}

export function ActiveConversationProvider({ paramId, children }: ProviderProps) {
  const navigate = useNavigate();
  const api = useApi();
  const getToken = useTokenGetter();
  const { create, upsertSummary } = useConversations();
  const { t } = useTranslation();

  const isNew = paramId === 'new';

  const [conversationId, setConversationId] = useState<string | null>(isNew ? null : paramId);
  const [messages, setMessages] = useState<MessageOut[]>([]);
  const [loading, setLoading] = useState<boolean>(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [runState, setRunState] = useState<RunState>('idle');
  const [runError, setRunError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAssistant | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  /** Guards against overlapping reconciles for one dropped run; also lets the
   * fetch-abort catch know a stop()-initiated reconcile already owns state. */
  const reconcileRef = useRef<boolean>(false);
  /** Live mirrors read by the visibilitychange listener (attached once) and by
   * reconcile's post-await guards, where state closures would be stale. */
  const cidRef = useRef<string | null>(conversationId);
  const runStateRef = useRef<RunState>(runState);
  /** Synchronous mirror of `pending`: SSE handlers need to read the latest
   * value during the same tick, and StrictMode double-invokes setState
   * updaters in dev so the updater itself can't be relied on for ordering.
   */
  const pendingRef = useRef<PendingAssistant | null>(null);
  /** Conversation id whose messages are currently in `messages`. Set after a
   * server fetch resolves, or directly during lazy-create (where we already
   * hold the messages locally). The effect below uses this to skip
   * redundant fetches — notably during the lazy-create URL transition.
   */
  const loadedConversationIdRef = useRef<string | null>(null);

  useEffect(() => {
    cidRef.current = conversationId;
  }, [conversationId]);
  useEffect(() => {
    runStateRef.current = runState;
  }, [runState]);

  useEffect(() => {
    if (paramId === 'new') {
      /** Coming from an existing conversation back to /c/new ("New chat").
       * No-op on initial mount because state is already at these defaults.
       */
      if (loadedConversationIdRef.current !== null) {
        loadedConversationIdRef.current = null;
        setConversationId(null);
        setMessages([]);
        setPending(null);
        pendingRef.current = null;
        setRunState('idle');
        setRunError(null);
        reconcileRef.current = false;
        setLoading(false);
        setLoadError(null);
        abortRef.current?.abort();
        abortRef.current = null;
      }
      return;
    }
    if (paramId === loadedConversationIdRef.current) return;

    let cancelled = false;
    setConversationId(paramId);
    setMessages([]);
    setPending(null);
    pendingRef.current = null;
    setRunState('idle');
    setRunError(null);
    reconcileRef.current = false;
    setLoading(true);
    setLoadError(null);
    abortRef.current?.abort();
    abortRef.current = null;

    api
      .getConversation(paramId)
      .then((conv) => {
        if (cancelled) return;
        setMessages(conv.messages);
        setLoading(false);
        loadedConversationIdRef.current = paramId;
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load conversation');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [paramId, api]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  /**
   * Recover a run whose live stream dropped (mobile background-suspend, transport
   * loss, or user stop). The backend keeps the run going and its state is
   * authoritative, so we poll GET /runs/{id} until terminal, then refetch-and-
   * replace the conversation. Idempotent via reconcileRef; guarded against the
   * user navigating to another conversation mid-flight via cidRef.
   */
  const reconcile = useCallback(
    async (cid: string, runId: string | null) => {
      if (reconcileRef.current) return;
      reconcileRef.current = true;

      // The live stream is gone. Replace its stale partial with the standard
      // "waiting for the reply" bubble (empty pending → typing dots), identical
      // to the pre-first-token wait; the full reply lands at once when the run
      // is terminal. runState stays 'streaming' so the stop button and the
      // conversation-switch lock stay engaged.
      const localId = pendingRef.current?.localId ?? makeLocalId('local-asst');
      pendingRef.current = { blocks: {}, runId, localId };
      setPending(pendingRef.current);
      setRunError(null);
      setRunState('streaming');

      // Settle into a terminal UI state from server truth. mergeMessages keeps
      // React keys stable (no remount flash); the freshly persisted assistant
      // reply is re-keyed to the pending bubble's localId so the typing-dots
      // bubble morphs into the message instead of remounting — the same
      // transition as a live stream finishing.
      const settle = async (next: RunState, errorKey: string | null) => {
        try {
          const conv = await api.getConversation(cid);
          if (cidRef.current === cid) {
            setMessages((prev) => {
              const merged = mergeMessages(prev, conv.messages);
              return runId
                ? merged.map((m) =>
                  m.role === 'assistant' && m.run_id === runId
                    ? { ...m, id: localId }
                    : m,
                )
                : merged;
            });
            loadedConversationIdRef.current = cid;
          }
        } catch {
          // Even the refetch failed (offline); fall through to the state below.
        }
        if (cidRef.current !== cid) return; // navigated away mid-reconcile
        pendingRef.current = null;
        setPending(null);
        setRunState(next);
        setRunError(errorKey ? t(errorKey) : null);
      };

      try {
        if (!runId) {
          // No run id to poll (run start failed, or we were suspended before
          // run_start). Show server truth and surface the interruption.
          await settle('error', 'errorRunFailed');
          return;
        }
        const deadline = Date.now() + RECONCILE_TIMEOUT_MS;
        while (true) {
          if (cidRef.current !== cid) {
            return;
          }
          let status: string;
          try {
            const run = await api.getRun(runId);
            status = run.status;
          } catch {
            if (Date.now() > deadline) {
              await settle('error', 'errorGeneric');
              return;
            }
            await sleep(RECONCILE_POLL_INTERVAL_MS);
            continue;
          }
          if (status === 'complete' || status === 'cancelled') {
            await settle('idle', null);
            return;
          }
          if (status === 'error') {
            await settle('error', 'errorRunFailed');
            return;
          }
          // running | cancelling — keep waiting until terminal or timeout.
          if (Date.now() > deadline) {
            await settle('error', 'errorRunFailed');
            return;
          }
          await sleep(RECONCILE_POLL_INTERVAL_MS);
        }
      } finally {
        reconcileRef.current = false;
      }
    },
    [api, t],
  );

  /** Multi-device freshness: pull messages sent from another device. Best
   * effort; only called when no run is active (see the visibilitychange
   * listener), so it never races a stream or reconcile. */
  const refetchActive = useCallback(
    async (cid: string) => {
      try {
        const conv = await api.getConversation(cid);
        if (cidRef.current !== cid) return;
        setMessages((prev) => mergeMessages(prev, conv.messages));
        loadedConversationIdRef.current = cid;
      } catch {
        // ignore — transient/offline; the next visibility or load will retry.
      }
    },
    [api],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (runState !== 'idle' && runState !== 'error') return;

      setRunState('sending');
      setRunError(null);

      /** Optimistic user bubble: render before the POST round-trip resolves.
       * We keep this client-side id stable for the session — when the POST
       * returns we merge the server's fields but keep the id, so React
       * doesn't remount the bubble.
       */
      const optimisticId = makeLocalId('local-user');
      const optimisticMsg: MessageOut = {
        id: optimisticId,
        role: 'user',
        turn_index: -1,
        content: [{ type: 'text', text: trimmed }],
        run_id: null,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimisticMsg]);

      /** Pre-allocate the assistant id and set `pending` synchronously, so
       * the streaming bubble shows instantly and shares its key with the
       * eventual finalized message (see PendingAssistant.localId).
       */
      const assistantLocalId = makeLocalId('local-asst');
      pendingRef.current = { blocks: {}, runId: null, localId: assistantLocalId };
      setPending(pendingRef.current);

      let userMessageConfirmed = false;
      let cid = conversationId;

      try {
        if (cid === null) {
          const conv = await create(null);
          cid = conv.id;
          setConversationId(cid);
          /** We hold the canonical messages locally, so the upcoming
           * /c/new → /c/{cid} URL change should skip the fetch path.
           */
          loadedConversationIdRef.current = cid;
          upsertSummary({
            id: conv.id,
            title: conv.title,
            app_id: conv.app_id,
            archived_at: conv.archived_at,
            created_at: conv.created_at,
            updated_at: conv.updated_at,
          });
        }

        /** Merge server fields into the optimistic row without touching its
         * id (the id is the React key that keeps the bubble stable).
         */
        const userMsg = await api.appendMessage(cid, {
          content: [{ type: 'text', text: trimmed }],
        });
        userMessageConfirmed = true;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === optimisticId
              ? {
                ...m,
                turn_index: userMsg.turn_index,
                created_at: userMsg.created_at,
                run_id: userMsg.run_id,
              }
              : m,
          ),
        );

        setRunState('streaming');

        const ac = new AbortController();
        abortRef.current = ac;

        const token = await getToken();

        await streamRun(
          cid,
          token,
          {
            onRunStart: (data) => {
              if (!pendingRef.current) return;
              pendingRef.current = { ...pendingRef.current, runId: data.run_id };
              setPending(pendingRef.current);
            },
            onBlockStart: (data) => {
              if (!pendingRef.current) return;
              const block: ContentBlock =
                data.block.type === 'text'
                  ? { type: 'text', text: '' }
                  : (data.block as ContentBlock);
              pendingRef.current = {
                ...pendingRef.current,
                blocks: { ...pendingRef.current.blocks, [data.index]: block },
              };
              setPending(pendingRef.current);
            },
            onBlockDelta: (data) => {
              const p = pendingRef.current;
              if (!p) return;
              const existing = p.blocks[data.index];
              if (!existing || existing.type !== 'text') return;
              const updated: ContentBlock = {
                ...existing,
                text: (existing as { text: string }).text + data.delta.text,
              };
              pendingRef.current = {
                ...p,
                blocks: { ...p.blocks, [data.index]: updated },
              };
              setPending(pendingRef.current);
            },
            onBlockStop: () => {
              /** Text blocks are accumulated in onBlockDelta; non-text
               * blocks complete in onBlockStart.
               */
            },
            onRunFinish: (data) => {
              const p = pendingRef.current;
              if (p) {
                const indices = Object.keys(p.blocks)
                  .map(Number)
                  .sort((a, b) => a - b);
                const content = indices
                  .map((i) => p.blocks[i])
                  .filter((b): b is ContentBlock => b !== undefined);

                const assistantMsg: MessageOut = {
                  /** Same id as the streaming bubble's React key — see
                   * PendingAssistant.localId.
                   */
                  id: p.localId,
                  role: 'assistant',
                  turn_index: -1,
                  content,
                  run_id: data.run_id,
                  created_at: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, assistantMsg]);
              }
              pendingRef.current = null;
              setPending(null);

              if (data.status === 'complete') {
                setRunState('idle');
              } else {
                setRunState('error');
                setRunError(t('errorRunFailed'));
              }

              if (isNew && cid) {
                navigate(`/c/${cid}`, { replace: true });
              }
            },
            onError: (data) => {
              setRunError(data.message || t('errorGeneric'));
              setRunState('error');
              pendingRef.current = null;
              setPending(null);
            },
          },
          ac.signal,
        );
      } catch (e) {
        const err = e as Error;
        if (err.name === 'AbortError') {
          /** Fetch aborted. Either stop() already kicked off cancellation +
           * reconcile (let it own state), or the provider is tearing down on
           * navigation/unmount (quiet cleanup).
           */
          if (!reconcileRef.current) {
            setRunState('idle');
            pendingRef.current = null;
            setPending(null);
          }
        } else if (!userMessageConfirmed) {
          /** POST /messages itself failed — roll back the optimistic user row. */
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
          setRunError(err.message || t('errorGeneric'));
          setRunState('error');
          pendingRef.current = null;
          setPending(null);
        } else if (pendingRef.current) {
          /** Stream dropped while the run was still unresolved (pending is still
           * set — onRunFinish/onError clear it synchronously). Reconcile against
           * the backend's true state instead of the raw transport error.
           *
           * If the stream instead threw *after* the run already resolved — e.g.
           * the connection closing right after a buffered run_finish is
           * processed on resume — pendingRef is already null and we ignore it,
           * avoiding a spurious "interrupted" error next to the delivered reply.
           */
          void reconcile(cid as string, pendingRef.current.runId);
        }
      } finally {
        abortRef.current = null;
      }
    },
    [api, conversationId, create, getToken, isNew, navigate, reconcile, runState, t, upsertSummary],
  );

  const stop = useCallback(() => {
    const cid = cidRef.current;
    const runId = pendingRef.current?.runId ?? null;
    if (cid && runId) {
      /** Cancel the run server-side (idempotent). Aborting the fetch no longer
       * cancels it, so we ask explicitly, then abort the local read and
       * reconcile to the authoritative terminal state.
       */
      void api.cancelRun(runId).catch(() => {
        // Best effort — reconcile reads the true status regardless.
      });
      abortRef.current?.abort();
      void reconcile(cid, runId);
    } else {
      /** No run id yet (stop pressed before run_start). Abort the local stream;
       * the run is about to start and will finalize on its own server-side.
       */
      abortRef.current?.abort();
    }
  }, [api, reconcile]);

  /**
   * On returning to a foregrounded tab: if a run was active when we left (mobile
   * suspends JS and drops the connection), reconcile it; otherwise pull any
   * messages sent from another device. Reads state via refs so the listener can
   * attach once. The reconcile is idempotent against the stream's own catch,
   * whichever fires first on resume.
   */
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const cid = cidRef.current;
      if (!cid) return;
      const state = runStateRef.current;
      if (state === 'streaming' && abortRef.current === null) {
        /** A run was streaming but its fetch has ended (abortRef cleared in
         * send's finally) without resolving to idle/error — i.e. the stream
         * died, the mobile background-suspend case. Reconcile as a backstop to
         * the stream's own catch (idempotent via reconcileRef).
         *
         * On desktop the fetch survives backgrounding, so a still-'streaming'
         * run has abortRef set (stream alive) and this is a no-op — we never
         * disturb a live stream.
         */
        void reconcile(cid, pendingRef.current?.runId ?? null);
      } else if (state === 'idle' || state === 'error') {
        /** No run active: pull anything sent from another device. */
        void refetchActive(cid);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [reconcile, refetchActive]);

  const value = useMemo<ActiveConversationContextValue>(
    () => ({
      conversationId,
      messages,
      loading,
      loadError,
      runState,
      runError,
      pending,
      send,
      stop,
    }),
    [conversationId, messages, loading, loadError, runState, runError, pending, send, stop],
  );

  return (
    <ActiveConversationContext.Provider value={value}>
      {children}
    </ActiveConversationContext.Provider>
  );
}

export function useActiveConversation(): ActiveConversationContextValue {
  const v = useContext(ActiveConversationContext);
  if (!v) throw new Error('useActiveConversation must be used inside ActiveConversationProvider');
  return v;
}
